// Profile Photo Synchronization Utilities

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated
 */
export const isUserAuthenticated = () => {
  const token = localStorage.getItem('authToken');
  const isLoggedIn = localStorage.getItem('isLoggedIn');
  return !!(token && isLoggedIn === 'true');
};

/**
 * Get authentication token
 * @returns {string|null} The authentication token or null
 */
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * Update profile photo across all components for a specific user
 * @param {string} photoUrl - The new profile photo URL (base64 or URL)
 * @param {string} customerName - The customer name for user-specific updates
 */
export const syncProfilePhotoAcrossAllComponents = (photoUrl, customerName) => {
  // Update localStorage for the specific user
  const photoKey = `uploadedProfilePhoto_${customerName}`;
  localStorage.setItem(photoKey, photoUrl);
  
  // Update all PC profile photos
  updatePCProfilePhotos(photoUrl);
  
  // Update all mobile profile photos
  updateMobileProfilePhotos(photoUrl);
  
  // Dispatch event to notify all components
  window.dispatchEvent(new CustomEvent('profilePhotoUpdated', {
    detail: {
      photoUrl: photoUrl,
      customerName: customerName
    }
  }));
};

/**
 * Update profile photos in all PC components
 * @param {string} photoUrl - The new profile photo URL
 */
const updatePCProfilePhotos = (photoUrl) => {
  // List of all PC profile photo selectors
  const pcSelectors = [
    '.donate-registration-profile-image img',
    '.memorial-post-board-list-profile-image-horizontal img',
    '.memorial-post-writing-profile-image-horizontal img',
    '.letter-to-heaven-board-list-profile-image-horizontal img',
    '.ancestral-tablet-profile-image-horizontal img',
    '.memorial-post-details-profile-image-horizontal img',
    '.donate-profile-image img',
    '.memorial-album-board-list-profile-image-horizontal img',
    '.memorial-album-writing-profile-image-horizontal img',
    '.memorial-album-article-details-profile-image-horizontal img'
  ];

  pcSelectors.forEach(selector => {
    const image = document.querySelector(selector);
    if (image) {
      image.src = photoUrl;
    }
  });
};

/**
 * Update profile photos in all mobile components
 * @param {string} photoUrl - The new profile photo URL
 */
const updateMobileProfilePhotos = (photoUrl) => {
  // List of all mobile profile photo selectors
  const mobileSelectors = [
    '.donate-mobile-group-image-unique img',
    '.memorial-post-board-list-mobile-group-image-unique img',
    '.memorial-post-writing-mobile-group-image-unique img',
    '.memorial-post-details-mobile-group-image-unique img',
    '.memorial-album-writing-mobile-group-image-unique img',
    '.memorial-album-board-list-mobile-group-image-unique img',
    '.memorial-album-article-details-mobile-group-image-unique img',
    '.letter-to-heaven-board-list-mobile-group-image-unique img',
    '.ancestral-tablet-mobile-group-image-unique img'
  ];

  mobileSelectors.forEach(selector => {
    const images = document.querySelectorAll(selector);
    images.forEach(img => {
      // Update if it's a group-129 image (default profile photo)
      if (img.src.includes('group-129') || img.src.includes('layer-1910')) {
        img.src = photoUrl;
      }
    });
  });
};

/**
 * Get current user's profile photo from localStorage
 * @returns {string|null} The profile photo URL or null if not found
 */
export const getCurrentUserProfilePhoto = () => {
  const selectedPersonData = localStorage.getItem('selectedPersonData');
  let customerName = 'default';
  
  if (selectedPersonData) {
    try {
      const personData = JSON.parse(selectedPersonData);
      customerName = personData.customerName || 'default';
    } catch (error) {
      customerName = 'default';
    }
  }
  
  const photoKey = `uploadedProfilePhoto_${customerName}`;
  return localStorage.getItem(photoKey);
};

/**
 * Get current user's customer name
 * @returns {string} The customer name
 */
export const getCurrentCustomerName = () => {
  const selectedPersonData = localStorage.getItem('selectedPersonData');
  let customerName = 'default';

  if (selectedPersonData) {
    try {
      const personData = JSON.parse(selectedPersonData);
      customerName = personData.customerName || 'default';
    } catch (error) {
      customerName = 'default';
    }
  }

  return customerName;
};

/**
 * Get the deceased person's name from selectedPersonData
 * @returns {string} The deceased person's name
 */
export const getDeceasedPersonName = () => {
  const selectedPersonData = localStorage.getItem('selectedPersonData');
  
  if (selectedPersonData) {
    try {
      const personData = JSON.parse(selectedPersonData);
      return personData.goinName || '故 홍길동';
    } catch (error) {
      return '故 홍길동';
    }
  }
  
  return '故 홍길동';
};

/**
 * Get the deceased person's date from selectedPersonData
 * @returns {string} The deceased person's date in Korean format
 */
export const getDeceasedPersonDate = () => {
  const selectedPersonData = localStorage.getItem('selectedPersonData');
  
  if (selectedPersonData) {
    try {
      const personData = JSON.parse(selectedPersonData);
      const dateString = personData.enshrineDay || personData.enshrineDate || '2011-01-01';
      
      // Convert date to Korean format (YYYY년 MM월 DD일)
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      
      return `${year}년 ${month.toString().padStart(2, '0')}월 ${day.toString().padStart(2, '0')}일 별세`;
    } catch (error) {
      return '2006년 11월 28일 별세';
    }
  }
  
  return '2006년 11월 28일 별세';
};

/**
 * Get the full profile text for the deceased person
 * @returns {string} The full profile text
 */
export const getDeceasedPersonProfileText = () => {
  const deceasedName = getDeceasedPersonName();
  return `${deceasedName}님의 사이버 추모관 입니다.`;
};

/**
 * Set up profile photo listener for a component
 * @param {Function} setProfilePhotoUrl - Function to update profile photo URL in component state
 * @param {string} customerName - The customer name to listen for
 */
export const setupProfilePhotoListener = (setProfilePhotoUrl, customerName) => {
  const handleProfilePhotoUpdate = (event) => {
    if (event.detail && event.detail.customerName === customerName) {
      setProfilePhotoUrl(event.detail.photoUrl);
    }
  };
  
  window.addEventListener('profilePhotoUpdated', handleProfilePhotoUpdate);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('profilePhotoUpdated', handleProfilePhotoUpdate);
  };
};

/**
 * Increment view count for a specific album and sync across all components
 * @param {number} albumId - The album ID
 * @param {number} currentReadCount - Current read count
 * @returns {number} The new read count
 */
export const incrementAlbumViewCount = (albumId, currentReadCount = 0) => {
  const newReadCount = currentReadCount + 1;
  
  // Store the updated view count in localStorage
  const viewCountKey = `albumViewCount_${albumId}`;
  localStorage.setItem(viewCountKey, newReadCount.toString());
  
  // Dispatch event to sync across all components
  window.dispatchEvent(new CustomEvent('albumViewCountUpdated', {
    detail: {
      albumId: albumId,
      readCount: newReadCount
    }
  }));
  
  return newReadCount;
};

/**
 * Get view count for a specific album
 * @param {number} albumId - The album ID
 * @param {number} defaultCount - Default count if not found
 * @returns {number} The view count
 */
export const getAlbumViewCount = (albumId, defaultCount = 0) => {
  const viewCountKey = `albumViewCount_${albumId}`;
  const storedCount = localStorage.getItem(viewCountKey);
  return storedCount ? parseInt(storedCount, 10) : defaultCount;
};

/**
 * Setup view count listener for real-time synchronization
 * @param {Function} setAlbums - Function to update albums state
 * @param {Array} albums - Current albums array
 * @returns {Function} Cleanup function
 */
export const setupViewCountListener = (setAlbums, albums) => {
  const handleViewCountUpdate = (event) => {
    const { albumId, readCount } = event.detail;
    
    setAlbums(prevAlbums => 
      prevAlbums.map(album => 
        album.albumId === albumId 
          ? { ...album, readCount: readCount }
          : album
      )
    );
  };

  window.addEventListener('albumViewCountUpdated', handleViewCountUpdate);
  
  return () => {
    window.removeEventListener('albumViewCountUpdated', handleViewCountUpdate);
  };
};

// Letter to Heaven Synchronization Utilities

/**
 * Save letter data with cross-platform synchronization
 * @param {Object} letterData - The letter data to save
 * @param {string} platform - 'PC' or 'Mobile'
 */
export const saveLetterData = (letterData, platform = 'PC') => {
  // Use unified key for cross-platform synchronization
  const unifiedKey = 'letterToHeavenData';
  const platformKey = `letterToHeavenData${platform}`;
  
  // Get existing letters from unified storage
  let existingLetters = [];
  const existingData = localStorage.getItem(unifiedKey);
  if (existingData) {
    try {
      const parsed = JSON.parse(existingData);
      // Handle both array and single object formats
      existingLetters = Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      console.error('Error parsing existing letter data:', error);
      existingLetters = [];
    }
  }
  
  // Check if letter already exists (for updates)
  const existingIndex = existingLetters.findIndex(letter => letter.id === letterData.id);
  
  if (existingIndex !== -1) {
    // Update existing letter
    existingLetters[existingIndex] = letterData;
  } else {
    // Add new letter to the beginning
    existingLetters.unshift(letterData);
  }
  
  // Save updated letters array to both keys
  localStorage.setItem(unifiedKey, JSON.stringify(existingLetters));
  localStorage.setItem(platformKey, JSON.stringify(existingLetters));
  
  // Dispatch event for real-time synchronization
  console.log('📨 Letter sync event dispatched:', {
    platform: platform,
    letterData: letterData,
    totalLetters: existingLetters.length
  });
  
  window.dispatchEvent(new CustomEvent('letterToHeavenUpdated', {
    detail: {
      letterData: letterData,
      platform: platform,
      allLetters: existingLetters
    }
  }));
};

/**
 * Migrate old letter data to new unified format
 */
const migrateOldLetterData = () => {
  const allLetters = [];
  
  // Check for old PC data
  const oldPCData = localStorage.getItem('letterToHeavenDataPC');
  if (oldPCData) {
    try {
      const parsed = JSON.parse(oldPCData);
      if (Array.isArray(parsed)) {
        allLetters.push(...parsed);
      } else if (parsed && parsed.id) {
        allLetters.push(parsed);
      }
    } catch (error) {
      console.error('Error parsing old PC data:', error);
    }
  }
  
  // Check for old Mobile data
  const oldMobileData = localStorage.getItem('letterToHeavenDataMobile');
  if (oldMobileData) {
    try {
      const parsed = JSON.parse(oldMobileData);
      if (Array.isArray(parsed)) {
        allLetters.push(...parsed);
      } else if (parsed && parsed.id) {
        allLetters.push(parsed);
      }
    } catch (error) {
      console.error('Error parsing old Mobile data:', error);
    }
  }
  
  // Remove duplicates based on ID
  const uniqueLetters = allLetters.filter((letter, index, self) => 
    index === self.findIndex(l => l.id === letter.id)
  );
  
  // Save to unified storage
  if (uniqueLetters.length > 0) {
    localStorage.setItem('letterToHeavenData', JSON.stringify(uniqueLetters));
    localStorage.setItem('letterToHeavenDataPC', JSON.stringify(uniqueLetters));
    localStorage.setItem('letterToHeavenDataMobile', JSON.stringify(uniqueLetters));
    
    console.log('🔄 Migrated old letter data:', uniqueLetters.length, 'letters');
    
    // Dispatch sync event to notify all components
    window.dispatchEvent(new CustomEvent('letterToHeavenUpdated', {
      detail: {
        letterData: uniqueLetters[0],
        platform: 'Migration',
        allLetters: uniqueLetters
      }
    }));
  }
  
  return uniqueLetters;
};

/**
 * Get user's IP address
 * @returns {string} IP address or default value
 */
export const getUserIPAddress = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('Could not fetch IP address, using default:', error);
    return '127.0.0.1';
  }
};

/**
 * Force synchronization of all existing letter data
 * This function can be called to ensure all data is synchronized
 */
export const syncAllExistingLetterData = () => {
  console.log('🔄 Starting full letter data synchronization...');
  
  // Migrate any old data first
  const migratedData = migrateOldLetterData();
  
  // Get all current data
  const allLetters = getLetterData();
  
  if (allLetters.length > 0) {
    // Save to all storage locations to ensure consistency
    localStorage.setItem('letterToHeavenData', JSON.stringify(allLetters));
    localStorage.setItem('letterToHeavenDataPC', JSON.stringify(allLetters));
    localStorage.setItem('letterToHeavenDataMobile', JSON.stringify(allLetters));
    
    // Dispatch sync event to notify all components
    window.dispatchEvent(new CustomEvent('letterToHeavenUpdated', {
      detail: {
        letterData: allLetters[0],
        platform: 'FullSync',
        allLetters: allLetters
      }
    }));
    
    console.log('✅ Full synchronization completed:', allLetters.length, 'letters');
  } else {
    console.log('ℹ️ No letter data found to synchronize');
  }
  
  return allLetters;
};

/**
 * Get all letter data with cross-platform support
 * @param {string} platform - 'PC' or 'Mobile' (optional)
 * @returns {Array} Array of letter data
 */
export const getLetterData = (platform = null) => {
  // Try unified key first
  const unifiedData = localStorage.getItem('letterToHeavenData');
  if (unifiedData) {
    try {
      const parsed = JSON.parse(unifiedData);
      // Handle both array and single object formats
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      console.error('Error parsing unified letter data:', error);
    }
  }
  
  // Fallback to platform-specific key if specified
  if (platform) {
    const platformKey = `letterToHeavenData${platform}`;
    const platformData = localStorage.getItem(platformKey);
    if (platformData) {
      try {
        const parsed = JSON.parse(platformData);
        // Handle both array and single object formats
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (error) {
        console.error(`Error parsing ${platform} letter data:`, error);
      }
    }
  }
  
  // If no data found, try to migrate old data
  const migratedData = migrateOldLetterData();
  if (migratedData.length > 0) {
    return migratedData;
  }
  
  return [];
};

/**
 * Setup letter synchronization listener
 * @param {Function} setLetters - Function to update letters state
 * @param {Array} letters - Current letters array
 * @returns {Function} Cleanup function
 */
export const setupLetterSyncListener = (setLetters, letters) => {
  const handleLetterUpdate = (event) => {
    const { letterData, platform, allLetters } = event.detail;
    
    console.log('📨 Letter sync event received:', {
      platform: platform,
      letterData: letterData,
      allLettersCount: allLetters ? allLetters.length : 0
    });
    
    // If we have allLetters, use that for complete sync
    if (allLetters && Array.isArray(allLetters)) {
      // Convert to display format
      const displayLetters = allLetters.map(letter => ({
        id: letter.id,
        to: letter.recipient,
        title: letter.title,
        sender: letter.sender,
        date: letter.date,
        selectedStamp: letter.selectedStamp
      }));
      console.log('📨 Updating letters with complete sync:', displayLetters.length, 'letters');
      setLetters(displayLetters);
    } else {
      // Fallback to individual letter update
      setLetters(prevLetters => {
        // Check if letter already exists
        const existingIndex = prevLetters.findIndex(letter => letter.id === letterData.id);
        
        if (existingIndex !== -1) {
          // Update existing letter
          return prevLetters.map((letter, index) => 
            index === existingIndex 
              ? {
                  id: letterData.id,
                  to: letterData.recipient,
                  title: letterData.title,
                  sender: letterData.sender,
                  date: letterData.date,
                  selectedStamp: letterData.selectedStamp
                }
              : letter
          );
        } else {
          // Add new letter to the beginning
          const newLetter = {
            id: letterData.id,
            to: letterData.recipient,
            title: letterData.title,
            sender: letterData.sender,
            date: letterData.date,
            selectedStamp: letterData.selectedStamp
          };
          return [newLetter, ...prevLetters];
        }
      });
    }
  };

  window.addEventListener('letterToHeavenUpdated', handleLetterUpdate);
  
  return () => {
    window.removeEventListener('letterToHeavenUpdated', handleLetterUpdate);
  };
};
